import React, { useEffect, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import { Box, Button } from '@mui/material';
import PropTypes from 'prop-types';
import DialogContent from '@mui/material/DialogContent';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import VpnKeyRoundedIcon from '@mui/icons-material/VpnKeyRounded';
import UserMainSettings from './UserSettingTabs/UserMainSettings';
import UserEmailNotificationSettings from './UserSettingTabs/UserEmailNotificationSettings';
import UserPermissionSettings from './UserSettingTabs/UserPermissionSettings';
import { UserAdminDto } from '../../../dtos/Users/UserAdminDto';
import UserChangePassword from './UserSettingTabs/UserChangePassword';
import UserApiService from '../../../api/UserApiService';
import { UserAdminMainSettingsDto } from '../../../dtos/Users/UserAdminMainSettingsDto';
import { UserAdminPermissionsDto } from '../../../dtos/Users/UserAdminPermissionsDto';
import { UserAdminEmailNotificationsDto } from '../../../dtos/Users/UserAdminEmailNotificationsDto';
import TabPanel from './../TabPanel';
import TaskQueryDto from '../../../dtos/TaskQueries/TaskQueryDto';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import AuthUtil from '../../Utils/AuthUtil';
import SaveButton from '../../Common/SaveButton';
import { useAsyncCallback } from '../../../hooks/useAsyncCallback';
import { useSnackbar } from '../../Common/SnackbarProvider';

export default function ModifyUserModal (props) {
    const { onClose, open, selectedUser, orgs, userApiService, setCurrentSelectedUser, onCancel, taskQueries, ...other } = props;
    const { t } = useTranslation();
    const snackbar = useSnackbar();

    const [tabValue, setTabValue] = useState(0);
    const [userPassword, setUserPassword] = useState(selectedUser?.password ?? '');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [newUserAdminMainSettings, setNewUserAdminMainSettings] = useState();
    const [newUserAdminPermissions, setNewUserAdminPermissions] = useState();
    const [newUserAdminEmailNotifications, setNewUserAdminEmailNotifications] = useState();
    const [applyNotificationChangesRetroactively, setApplyNotificationChangesRetroactively] = useState(false);
    const [isOrgAdmin, setIsOrgAdmin] = useState(false);

    // Validation Errors
    const [orgError, setOrgError] = useState(false);
    const [usernameError, setUsernameError] = useState(false);
    const [passwordError, setPasswordError] = useState(false);
    const [firstNameError, setFirstNameError] = useState(false);
    const [lastNameError, setLastNameError] = useState(false);

    const userToken = useSelector((state) => state.correcttrack.userToken);
    const isSuperAdmin = useMemo(() => AuthUtil.isSuperAdmin(userToken), [userToken]);

    function a11yProps (index) {
        return {
            id: `simple-tab-${index}`,
            'aria-controls': `simple-tabpanel-${index}`,
        };
    }

    function handleCancel () {
        onCancel();
        setTabValue(0);
    }

    const { run: handleSave, loading: saving } = useAsyncCallback(async () => {
        if (selectedUser) {
            const updatedUser = JSON.parse(JSON.stringify(selectedUser));
            updatedUser.mainSettingsDto = newUserAdminMainSettings;
            updatedUser.mainSettingsDto.usActive = newUserAdminMainSettings?.usActive ? 1 : 0;
            updatedUser.permissionsDto = newUserAdminPermissions;
            updatedUser.emailNotificationsDto = newUserAdminEmailNotifications;
            const userUpdateResponse = await userApiService.saveUser(updatedUser);
            if (userUpdateResponse && userUpdateResponse.success) {
                snackbar.success(t('modifyUser.toast.updated'));
                setCurrentSelectedUser(updatedUser);
                onClose(userUpdateResponse.data);
            } else {
                snackbar.error(t('modifyUser.toast.updateError', { error: userUpdateResponse.message }));
            }
        } else {
            if (!validateNewUser()) {
                return;
            }
            if (userPassword === '') {
                snackbar.error(t('modifyUser.validation.passwordRequired'));
            }
            if (userPassword === confirmPassword) {
                const newUser = createNewUserFromInfo();
                newUser.mainSettingsDto.password = userPassword;
                const addUserResponse = await userApiService.addUser(newUser);

                if (addUserResponse && addUserResponse.success) {
                    snackbar.success(t('modifyUser.toast.added'));
                    onClose(addUserResponse.data);
                } else {
                    snackbar.error(t('modifyUser.toast.addError', { error: addUserResponse.message }));
                }
            }
        }
    });

    function validateNewUser () {
        const errors = [];

        if (!newUserAdminMainSettings.usOrg) {
            errors.push(t('userMainSettings.validation.orgRequired'));
        }
        setOrgError(!newUserAdminMainSettings.usOrg);

        if (!newUserAdminMainSettings.usFirstname) {
            errors.push(t('userMainSettings.validation.firstNameRequired'));
        }
        setFirstNameError(!newUserAdminMainSettings.usFirstname);

        if (!newUserAdminMainSettings.usLastname) {
            errors.push(t('userMainSettings.validation.lastNameRequired'));
        }
        setLastNameError(!newUserAdminMainSettings.usLastname);

        if (!newUserAdminMainSettings.usUsername) {
            errors.push(t('userMainSettings.validation.usernameRequired'));
        }
        setUsernameError(!newUserAdminMainSettings.usUsername);

        if (!userPassword) {
            errors.push(t('userMainSettings.validation.passwordRequired'));
        } else if (userPassword !== confirmPassword) {
            errors.push(t('userMainSettings.validation.passwordsMustMatch'));
        }
        setPasswordError(!userPassword || !confirmPassword || userPassword !== confirmPassword);

        if (errors.length > 0) {
            snackbar.error(t('modifyUser.validation.newUserFailed', { errors: errors.join(', ') }));
            return false;
        } else {
            return true;
        }
    }

    function createNewUserFromInfo () {
        const newUser = new UserAdminDto();
        newUser.mainSettingsDto = newUserAdminMainSettings;
        newUser.permissionsDto = newUserAdminPermissions;
        newUser.emailNotificationsDto = newUserAdminEmailNotifications;
        return newUser;
    }

    async function checkProjectUserXRefs (orgId) {
        if (!selectedUser) {
            const newProjectUserXRefs = await userApiService.getProjectUserXRefsForNewUser(orgId);
            if (newProjectUserXRefs && newProjectUserXRefs.success) {
                const updatedSettings = { ...newUserAdminPermissions };
                updatedSettings.projectUserXRefs = newProjectUserXRefs.data;
                setNewUserAdminPermissions(updatedSettings);
            }
        }
    }

    function handleChange (event, newValue) {
        setTabValue(newValue);
    }

    useEffect(() => {
        if (open) {
            setTabValue(0);
            setNewUserAdminMainSettings(selectedUser?.mainSettingsDto ?? new UserAdminMainSettingsDto());
            setNewUserAdminPermissions(selectedUser?.permissionsDto ?? new UserAdminPermissionsDto());
            setNewUserAdminEmailNotifications(selectedUser?.emailNotificationsDto ?? new UserAdminEmailNotificationsDto());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    return (
        <Dialog
            closeAfterTransition={true}
            maxWidth="md"
            fullWidth
            open={open}
            {...other}
        >
            <DialogTitle>{selectedUser?.usUsername ? t('modifyUser.title.edit', { name: selectedUser.usUsername }) : t('modifyUser.title.create')}</DialogTitle>
            <DialogContent dividers>
                <Box>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                        <Tabs
                            value={tabValue}
                            onChange={handleChange}
                            variant="scrollable"
                            allowScrollButtonsMobile
                        >
                            <Tab icon={<PersonOutlineRoundedIcon fontSize="small" />} iconPosition="start" label={t('userTabs.mainSettings')} {...a11yProps(0)} sx={{ minHeight: 48 }} />
                            <Tab icon={<NotificationsNoneRoundedIcon fontSize="small" />} iconPosition="start" label={t('userTabs.emailNotifications')} {...a11yProps(1)} sx={{ minHeight: 48 }} />
                            <Tab icon={<SecurityRoundedIcon fontSize="small" />} iconPosition="start" label={t('userTabs.permissions')} {...a11yProps(2)} sx={{ minHeight: 48 }} />
                            {Boolean(selectedUser) && <Tab icon={<VpnKeyRoundedIcon fontSize="small" />} iconPosition="start" label={t('userTabs.changePassword')} {...a11yProps(3)} sx={{ minHeight: 48 }} />}
                        </Tabs>
                    </Box>
                    <TabPanel
                        value={tabValue}
                        index={0}
                    >
                        <UserMainSettings
                            userAdminMainSettings={newUserAdminMainSettings}
                            setUserAdminMainSettings={setNewUserAdminMainSettings}
                            userPassword={userPassword}
                            setUserPassword={setUserPassword}
                            confirmPassword={confirmPassword}
                            setConfirmPassword={setConfirmPassword}
                            orgs={orgs}
                            userId={selectedUser?.usId ?? 0}
                            isCurrentUserSuperAdmin={isSuperAdmin}
                            checkProjectUserXRefs={checkProjectUserXRefs}
                            orgError={orgError}
                            usernameError={usernameError}
                            passwordError={passwordError}
                            firstNameError={firstNameError}
                            lastNameError={lastNameError}
                            isOrgAdmin={isOrgAdmin}
                            setIsOrgAdmin={setIsOrgAdmin}
                            editAdmin={true}
                        />
                    </TabPanel>
                    <TabPanel
                        value={tabValue}
                        index={1}
                    >
                        <UserEmailNotificationSettings
                            userAdminEmailNotification={newUserAdminEmailNotifications}
                            setNewUserAdminEmailNotification={setNewUserAdminEmailNotifications}
                            applyNotificationChangesRetroactively={applyNotificationChangesRetroactively}
                            setApplyNotificationChangesRetroactively={setApplyNotificationChangesRetroactively}
                        />
                    </TabPanel>
                    <TabPanel
                        value={tabValue}
                        index={2}
                    >
                        <UserPermissionSettings
                            userAdminPermissions={newUserAdminPermissions}
                            setUserAdminPermissions={setNewUserAdminPermissions}
                        />
                    </TabPanel>
                    {Boolean(selectedUser) &&
                        <TabPanel
                            value={tabValue}
                            index={3}
                        >
                            <UserChangePassword
                                user={selectedUser}
                            />
                        </TabPanel>
                    }

                </Box>
            </DialogContent>
            <DialogActions>
                <Button
                    autoFocus
                    variant='contained'
                    onClick={handleCancel}
                    disabled={saving}
                >
                    {t('common.cancel')}
                </Button>
                <SaveButton
                    loading={saving}
                    variant='contained'
                    onClick={handleSave}
                    disabled={tabValue === 3}
                >
                    {t('common.save')}
                </SaveButton>
            </DialogActions>
        </Dialog>
    );
}

ModifyUserModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    orgs: PropTypes.array,
    selectedUser: PropTypes.objectOf(UserAdminDto),
    userApiService: PropTypes.objectOf(UserApiService).isRequired,
    setCurrentSelectedUser: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    taskQueries: PropTypes.arrayOf(TaskQueryDto),
};
